/** Inline styles a text segment can carry when rendered as markdown. */
export interface TextSegmentStyle {
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
}

export type LinkifySegment =
  | ({ type: 'text'; value: string } & TextSegmentStyle)
  | {
      type: 'link';
      value: string;
      href: string;
      label: string;
      kind: 'url' | 'email';
    };

const LINK_PATTERN = /\b(?:https?:\/\/[^\s<>"']+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;

/** Splits plain text into text and clickable link/email segments. */
export function linkifyText(text: string): LinkifySegment[] {
  if (!text) {
    return [];
  }

  const segments: LinkifySegment[] = [];
  let lastIndex = 0;
  const regex = new RegExp(LINK_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const rawMatch = trimTrailingPunctuation(match[0]);
    const start = match.index;

    if (!rawMatch) {
      continue;
    }

    if (start > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, start) });
    }

    if (isEmailSegment(rawMatch)) {
      segments.push({
        type: 'link',
        value: rawMatch,
        href: `mailto:${rawMatch}`,
        label: rawMatch,
        kind: 'email',
      });
    } else {
      const href = rawMatch.startsWith('http') ? rawMatch : `https://${rawMatch}`;
      segments.push({
        type: 'link',
        value: rawMatch,
        href,
        label: formatLinkLabel(href),
        kind: 'url',
      });
    }

    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: 'text', value: text }];
}

function isEmailSegment(value: string): boolean {
  return value.includes('@') && !value.startsWith('http');
}

function trimTrailingPunctuation(value: string): string {
  return value.replace(/[.,;:!?)]+$/g, '');
}

function formatLinkLabel(href: string): string {
  try {
    const url = new URL(href);
    const host = url.hostname.replace(/^www\./, '');

    if (host.includes('instagram.com')) {
      return 'Instagram';
    }
    if (host.includes('facebook.com')) {
      return 'Facebook';
    }
    if (host.includes('twitter.com') || host === 'x.com') {
      return 'X (Twitter)';
    }
    if (host.includes('linkedin.com')) {
      return 'LinkedIn';
    }

    const path = url.pathname.replace(/\/$/, '');
    if (path && path !== '/') {
      const pathLabel = path.length > 24 ? `${path.slice(0, 24)}…` : path;
      return `${host}${pathLabel}`;
    }

    return host;
  } catch {
    return href.length > 48 ? `${href.slice(0, 45)}…` : href;
  }
}
